// Client-side validation for the public admission form. Deliberately mirrors
// the rules the backend enforces (internal/classroom/admission.go) so a parent
// finds problems while typing rather than after submitting — the server stays
// the authority.

export const GENDERS = ['Male', 'Female'];

export const PARENT_RELATIONSHIPS = [
  'Father',
  'Mother',
  'Guardian',
  'Grandfather',
  'Grandmother',
  'Uncle',
  'Aunt',
  'Brother',
  'Sister',
];

export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_DOCUMENTS = 6;
export const ACCEPTED_DOCUMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];

const EMAIL_PATTERN = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

/** Digits and at most one leading +, matching what the backend stores. */
export const normalizePhone = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '').slice(0, 15);
  return hasPlus ? `+${digits}` : digits;
};

export const isValidPhone = (value) => {
  const digits = normalizePhone(value).replace(/^\+/, '');
  return digits.length >= 7 && digits.length <= 15;
};

export const isValidEmail = (value) => EMAIL_PATTERN.test(value.trim());

/** Human file size for the file list and error messages. */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const emptyAdmissionValues = {
  studentName: '',
  gender: '',
  dateOfBirth: '',
  nationality: '',
  address: '',
  previousSchool: '',
  preferredStartDate: '',

  specialNeeds: '',
  medicalConditions: '',
  allergens: '',

  parentName: '',
  parentRelationship: '',
  parentPhone: '',
  parentEmail: '',
  parentOccupation: '',

  hasSecondParent: false,
  parentTwoName: '',
  parentTwoRelationship: '',
  parentTwoPhone: '',
  parentTwoEmail: '',
  parentTwoOccupation: '',

  admissionNote: '',
};

const validateStudent = (values) => {
  const errors = {};

  if (!values.studentName.trim()) {
    errors.studentName = "Enter the student's name.";
  }
  if (!GENDERS.includes(values.gender)) {
    errors.gender = "Select the student's gender.";
  }
  if (values.dateOfBirth) {
    const dob = new Date(values.dateOfBirth);
    if (Number.isNaN(dob.getTime())) {
      errors.dateOfBirth = 'Enter a valid date of birth.';
    } else if (dob > new Date()) {
      errors.dateOfBirth = 'Date of birth cannot be in the future.';
    } else if (dob.getFullYear() < 1900) {
      errors.dateOfBirth = 'Enter a valid date of birth.';
    }
  }
  return errors;
};

const validateParents = (values) => {
  const errors = {};

  if (!values.parentName.trim()) {
    errors.parentName = "Enter the parent or guardian's name.";
  }
  if (!PARENT_RELATIONSHIPS.includes(values.parentRelationship)) {
    errors.parentRelationship = 'Select their relationship to the student.';
  }
  // Always required on the public form: it is how the school follows up.
  if (!values.parentPhone.trim()) {
    errors.parentPhone = 'Enter a phone number the school can reach you on.';
  } else if (!isValidPhone(values.parentPhone)) {
    errors.parentPhone = 'Enter a valid phone number, including the country code.';
  }
  if (values.parentEmail.trim() && !isValidEmail(values.parentEmail)) {
    errors.parentEmail = 'Enter a valid email address, or leave it blank.';
  }

  if (values.hasSecondParent) {
    if (!values.parentTwoName.trim()) {
      errors.parentTwoName = "Enter the second parent or guardian's name, or remove them.";
    }
    if (!PARENT_RELATIONSHIPS.includes(values.parentTwoRelationship)) {
      errors.parentTwoRelationship = 'Select their relationship to the student.';
    }
    if (values.parentTwoPhone.trim() && !isValidPhone(values.parentTwoPhone)) {
      errors.parentTwoPhone = 'Enter a valid phone number, including the country code.';
    }
    if (values.parentTwoEmail.trim() && !isValidEmail(values.parentTwoEmail)) {
      errors.parentTwoEmail = 'Enter a valid email address, or leave it blank.';
    }
  }
  return errors;
};

/** Medical answers are all optional — nothing is forced when it does not apply. */
const validateMedical = () => ({});

const validateDetails = () => ({});

const VALIDATORS = {
  student: validateStudent,
  medical: validateMedical,
  parents: validateParents,
  details: validateDetails,
  review: (values) => ({
    ...validateStudent(values),
    ...validateParents(values),
  }),
};

/** Errors for one step, keyed by field name. Empty object means the step is good. */
export const validateStep = (stepId, values) => (VALIDATORS[stepId] || (() => ({})))(values);

/** Check a chosen file before it is ever added to the form. */
export const validateFile = (file, { isImage = false, existing = [] } = {}) => {
  const accepted = isImage ? ACCEPTED_IMAGE_TYPES : ACCEPTED_DOCUMENT_TYPES;

  if (!accepted.includes(file.type)) {
    return isImage
      ? 'The student photo must be a JPG or PNG image.'
      : `${file.name} is not supported. Attach a JPG, PNG or PDF.`;
  }
  if (file.size > MAX_FILE_BYTES) {
    return `${file.name} is ${formatFileSize(file.size)}. The limit is 5 MB.`;
  }
  if (file.size === 0) {
    return `${file.name} is empty. Choose a different file.`;
  }
  const duplicate = existing.some(
    (item) => item.file.name === file.name && item.file.size === file.size
  );
  if (duplicate) {
    return `${file.name} has already been attached.`;
  }
  return null;
};
